import { BEAD_SPECS } from '../domain/beads';
import { contrastInk, mixHex } from '../domain/color';
import { symbolForIndex } from '../domain/pattern';
import { layoutFor, wovenRows } from '../domain/stitches';
import type { Layout } from '../domain/stitches';
import { EMPTY } from '../domain/types';
import type { Cell, Pattern, Rect } from '../domain/types';
import type { Op } from '../editor/draw';
import type { Floating, ViewState } from '../editor/store';

export const GUTTER = 26;

export interface RenderInput {
  pattern: Pattern;
  view: ViewState;
  preview: Op[];
  selection: Rect | null;
  floating: Floating | null;
  hover: Cell | null;
  reference: { image: HTMLImageElement; opacity: number; x: number; y: number; scale: number } | null;
  /** CSS pixel size of the canvas. */
  width: number;
  height: number;
  theme: ChartTheme;
}

export interface ChartTheme {
  background: string;
  gutter: string;
  gutterText: string;
  grid: string;
  guide: string;
  emptyBead: string;
  outline: string;
  selection: string;
  thread: string;
}

export const DARK_THEME: ChartTheme = {
  background: '#1b1d23',
  gutter: '#22252d',
  gutterText: '#8b93a5',
  grid: 'rgba(255,255,255,0.10)',
  guide: 'rgba(255,255,255,0.28)',
  emptyBead: 'rgba(255,255,255,0.05)',
  outline: 'rgba(0,0,0,0.45)',
  selection: '#4da3ff',
  thread: 'rgba(255,220,120,0.85)',
};

export const LIGHT_THEME: ChartTheme = {
  background: '#f3f4f7',
  gutter: '#e7e9ef',
  gutterText: '#5b6376',
  grid: 'rgba(0,0,0,0.12)',
  guide: 'rgba(0,0,0,0.32)',
  emptyBead: 'rgba(0,0,0,0.04)',
  outline: 'rgba(0,0,0,0.30)',
  selection: '#1a72d6',
  thread: 'rgba(190,90,20,0.85)',
};

/** Grid cell under a screen point, accounting for the ruler gutter and pan. */
export function cellAtScreen(
  pattern: Pattern,
  view: ViewState,
  sx: number,
  sy: number,
  allowOutside = false,
): Cell | null {
  const layout = layoutFor(pattern);
  const gutter = view.showRulers ? GUTTER : 0;
  const mmX = (sx - gutter - view.panX) / view.zoom;
  const mmY = (sy - gutter - view.panY) / view.zoom;
  return allowOutside ? layout.cellAtUnclamped(mmX, mmY) : layout.cellAt(mmX, mmY);
}

/** Top-left screen position of a cell, in CSS pixels. */
export function cellToScreen(
  layout: Layout,
  view: ViewState,
  col: number,
  row: number,
): { x: number; y: number; w: number; h: number } {
  const gutter = view.showRulers ? GUTTER : 0;
  const p = layout.pos(col, row);
  return {
    x: gutter + view.panX + p.x * view.zoom,
    y: gutter + view.panY + p.y * view.zoom,
    w: layout.cellW * view.zoom,
    h: layout.cellH * view.zoom,
  };
}

function drawBead(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  hex: string,
  shape: 'cylinder' | 'round' | 'cube',
  realistic: boolean,
  theme: ChartTheme,
): void {
  const pad = Math.min(w, h) * (realistic ? 0.04 : 0.02);
  const bx = x + pad;
  const by = y + pad;
  const bw = Math.max(1, w - pad * 2);
  const bh = Math.max(1, h - pad * 2);

  if (bw < 3 || bh < 3) {
    // Too small for detail — flat fill keeps large patterns responsive.
    ctx.fillStyle = hex;
    ctx.fillRect(x, y, w + 0.5, h + 0.5);
    return;
  }

  const radius =
    shape === 'round'
      ? Math.min(bw, bh) / 2
      : shape === 'cube'
        ? Math.min(bw, bh) * 0.18
        : // Cylinders sit almost flush against each other; too much rounding
          // leaves bright diamond gaps between beads on a light background.
          Math.min(bw, bh) * 0.22;

  ctx.beginPath();
  ctx.roundRect(bx, by, bw, bh, radius);

  if (realistic) {
    const grad = ctx.createLinearGradient(bx, by, bx, by + bh);
    grad.addColorStop(0, mixHex(hex, '#ffffff', 0.34));
    grad.addColorStop(0.42, hex);
    grad.addColorStop(1, mixHex(hex, '#000000', 0.3));
    ctx.fillStyle = grad;
  } else {
    ctx.fillStyle = hex;
  }
  ctx.fill();

  if (realistic && bw >= 6 && bh >= 6) {
    // Specular streak plus a darker hole, which is what makes a flat grid of
    // rectangles read as actual beads.
    ctx.save();
    ctx.clip();
    ctx.fillStyle = 'rgba(255,255,255,0.30)';
    ctx.beginPath();
    ctx.ellipse(bx + bw * 0.32, by + bh * 0.24, bw * 0.2, bh * 0.12, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = 'rgba(0,0,0,0.28)';
    ctx.beginPath();
    if (shape === 'round') {
      ctx.ellipse(bx + bw / 2, by + bh / 2, bw * 0.13, bh * 0.13, 0, 0, Math.PI * 2);
    } else {
      ctx.ellipse(bx + bw / 2, by + bh / 2, bw * 0.1, bh * 0.16, 0, 0, Math.PI * 2);
    }
    ctx.fill();
    ctx.restore();
  }

  if (Math.min(bw, bh) > 4) {
    ctx.strokeStyle = theme.outline;
    ctx.lineWidth = 1;
    ctx.stroke();
  }
}

export function renderChart(ctx: CanvasRenderingContext2D, input: RenderInput): void {
  const { pattern, view, theme, width, height } = input;
  const layout = layoutFor(pattern);
  const spec = BEAD_SPECS[pattern.beadSize];
  const gutter = view.showRulers ? GUTTER : 0;
  const cw = layout.cellW * view.zoom;
  const ch = layout.cellH * view.zoom;

  ctx.save();
  ctx.fillStyle = theme.background;
  ctx.fillRect(0, 0, width, height);

  // Clip the bead area so nothing spills into the rulers.
  ctx.save();
  ctx.beginPath();
  ctx.rect(gutter, gutter, Math.max(0, width - gutter), Math.max(0, height - gutter));
  ctx.clip();
  ctx.translate(gutter + view.panX, gutter + view.panY);
  ctx.scale(view.zoom, view.zoom);

  if (input.reference) {
    const r = input.reference;
    ctx.save();
    ctx.globalAlpha = r.opacity;
    const iw = r.image.naturalWidth * r.scale * layout.cellW;
    const ih = r.image.naturalHeight * r.scale * layout.cellH;
    ctx.drawImage(r.image, r.x * layout.cellW, r.y * layout.cellH, iw, ih);
    ctx.restore();
  }
  ctx.restore();

  // Visible cell range, so a 400x400 pattern only pays for what is on screen.
  const mmLeft = (-view.panX) / view.zoom;
  const mmTop = (-view.panY) / view.zoom;
  const mmRight = (width - gutter - view.panX) / view.zoom;
  const mmBottom = (height - gutter - view.panY) / view.zoom;
  const colStart = Math.max(0, Math.floor(mmLeft / layout.cellW) - 2);
  const colEnd = Math.min(pattern.width - 1, Math.ceil(mmRight / layout.cellW) + 2);
  const rowStart = Math.max(0, Math.floor(mmTop / layout.cellH) - 2);
  const rowEnd = Math.min(pattern.height - 1, Math.ceil(mmBottom / layout.cellH) + 2);

  const previewMap = new Map<number, number>();
  for (const op of input.preview) previewMap.set(op.row * pattern.width + op.col, op.value);

  const floatMap = new Map<number, number>();
  if (input.floating) {
    const f = input.floating;
    for (let r = 0; r < f.h; r++) {
      for (let c = 0; c < f.w; c++) {
        const v = f.cells[r * f.w + c];
        if (v === EMPTY) continue;
        const tr = f.y + r;
        const tc = f.x + c;
        if (tr < 0 || tr >= pattern.height || tc < 0 || tc >= pattern.width) continue;
        floatMap.set(tr * pattern.width + tc, v);
      }
    }
  }

  ctx.save();
  ctx.beginPath();
  ctx.rect(gutter, gutter, Math.max(0, width - gutter), Math.max(0, height - gutter));
  ctx.clip();

  const realistic = view.mode === 'realistic';
  const showSymbols = view.showSymbols && Math.min(cw, ch) >= 10;
  if (showSymbols) {
    ctx.font = `${Math.floor(Math.min(cw, ch) * 0.62)}px ui-sans-serif, system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
  }

  for (let row = rowStart; row <= rowEnd; row++) {
    for (let col = colStart; col <= colEnd; col++) {
      const key = row * pattern.width + col;
      let value = pattern.cells[key];
      const floated = floatMap.get(key);
      if (floated !== undefined) value = floated;
      const prev = previewMap.get(key);
      if (prev !== undefined) value = prev;

      const s = cellToScreen(layout, view, col, row);
      if (value === EMPTY) {
        if (view.showGrid) {
          ctx.fillStyle = theme.emptyBead;
          ctx.fillRect(s.x, s.y, s.w, s.h);
          if (Math.min(cw, ch) > 4) {
            ctx.strokeStyle = theme.grid;
            ctx.lineWidth = 1;
            ctx.strokeRect(s.x + 0.5, s.y + 0.5, s.w - 1, s.h - 1);
          }
        }
        continue;
      }
      const color = pattern.palette[value];
      const hex = color?.hex ?? '#ff00ff';
      drawBead(ctx, s.x, s.y, s.w, s.h, hex, spec.shape, realistic, theme);
      if (showSymbols) {
        ctx.fillStyle = contrastInk(hex);
        ctx.fillText(symbolForIndex(value), s.x + s.w / 2, s.y + s.h / 2 + 0.5);
      }
    }
  }

  if (view.showGuides && Math.min(cw, ch) > 5) {
    ctx.strokeStyle = theme.guide;
    ctx.lineWidth = 1;
    ctx.beginPath();
    const originX = gutter + view.panX;
    const originY = gutter + view.panY;
    for (let col = 0; col <= pattern.width; col += 10) {
      const mm = col * layout.cellW + Math.floor(col / 2) * layout.pairGap;
      const x = Math.round(originX + mm * view.zoom) + 0.5;
      if (x < gutter || x > width) continue;
      ctx.moveTo(x, gutter);
      ctx.lineTo(x, height);
    }
    // Guides are counted from the bottom so they line up with the row numbers.
    for (let n = 0; n <= pattern.height; n += 10) {
      const y = Math.round(originY + (pattern.height - n) * layout.cellH * view.zoom) + 0.5;
      if (y < gutter || y > height) continue;
      ctx.moveTo(gutter, y);
      ctx.lineTo(width, y);
    }
    ctx.stroke();
  }

  if (view.showThread) drawThreadPath(ctx, pattern, layout, view, theme);

  if (input.hover && !input.floating) {
    const s = cellToScreen(layout, view, input.hover.col, input.hover.row);
    ctx.strokeStyle = theme.selection;
    ctx.lineWidth = 2;
    ctx.strokeRect(s.x + 1, s.y + 1, s.w - 2, s.h - 2);
  }

  if (input.selection) {
    const sel = input.selection;
    const a = cellToScreen(layout, view, sel.x, sel.y);
    const b = cellToScreen(layout, view, sel.x + sel.w - 1, sel.y + sel.h - 1);
    const x = Math.min(a.x, b.x);
    const y = Math.min(a.y, b.y);
    const w = Math.max(a.x + a.w, b.x + b.w) - x;
    const h = Math.max(a.y + a.h, b.y + b.h) - y;
    ctx.save();
    ctx.strokeStyle = theme.selection;
    ctx.lineWidth = 1.5;
    ctx.setLineDash([5, 4]);
    ctx.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);
    ctx.restore();
  }

  ctx.restore();

  if (view.showRulers) drawRulers(ctx, pattern, layout, view, theme, width, height);
  ctx.restore();
}

function drawThreadPath(
  ctx: CanvasRenderingContext2D,
  pattern: Pattern,
  layout: Layout,
  view: ViewState,
  theme: ChartTheme,
): void {
  const rows = wovenRows(pattern);
  ctx.save();
  ctx.strokeStyle = theme.thread;
  ctx.lineWidth = Math.max(1, Math.min(3, view.zoom * 0.18));
  ctx.lineJoin = 'round';
  ctx.beginPath();
  let started = false;
  for (const wr of rows) {
    for (const cell of wr.cells) {
      if (pattern.cells[cell.row * pattern.width + cell.col] === EMPTY) continue;
      const s = cellToScreen(layout, view, cell.col, cell.row);
      const cx = s.x + s.w / 2;
      const cy = s.y + s.h / 2;
      if (!started) {
        ctx.moveTo(cx, cy);
        started = true;
      } else {
        ctx.lineTo(cx, cy);
      }
    }
  }
  ctx.stroke();
  ctx.restore();
}

function drawRulers(
  ctx: CanvasRenderingContext2D,
  pattern: Pattern,
  layout: Layout,
  view: ViewState,
  theme: ChartTheme,
  width: number,
  height: number,
): void {
  ctx.save();
  ctx.fillStyle = theme.gutter;
  ctx.fillRect(0, 0, width, GUTTER);
  ctx.fillRect(0, 0, GUTTER, height);
  ctx.fillStyle = theme.gutterText;
  ctx.font = '10px ui-monospace, SFMono-Regular, monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  const cw = layout.cellW * view.zoom;
  const ch = layout.cellH * view.zoom;
  const colStep = cw < 14 ? (cw < 6 ? 20 : 10) : 5;
  const rowStep = ch < 14 ? (ch < 6 ? 20 : 10) : 5;

  // The first and last numbers are always useful, but they must not collide
  // with the regular interval marks sitting next to them.
  let lastX = -Infinity;
  for (let col = 0; col < pattern.width; col++) {
    const n = col + 1;
    if (n !== 1 && n % colStep !== 0 && n !== pattern.width) continue;
    const s = cellToScreen(layout, view, col, 0);
    const x = s.x + s.w / 2;
    if (x < GUTTER - 4 || x > width + 4) continue;
    // Wide enough for a three-digit number plus a gap.
    if (x - lastX < 28) continue;
    lastX = x;
    ctx.fillText(String(n), x, GUTTER / 2);
  }

  ctx.textAlign = 'center';
  let lastY = -Infinity;
  for (let row = pattern.height - 1; row >= 0; row--) {
    // Rows are numbered from the bottom, the way beadwork is counted.
    const n = pattern.height - row;
    if (n !== 1 && n % rowStep !== 0 && n !== pattern.height) continue;
    const s = cellToScreen(layout, view, 0, row);
    const y = s.y + s.h / 2;
    if (y < GUTTER - 4 || y > height + 4) continue;
    if (Math.abs(y - lastY) < 12) continue;
    lastY = y;
    ctx.fillText(String(n), GUTTER / 2, y);
  }

  ctx.fillStyle = theme.gutter;
  ctx.fillRect(0, 0, GUTTER, GUTTER);
  ctx.restore();
}

/** Zoom and pan that fit the whole pattern into a viewport. */
export function fitView(pattern: Pattern, width: number, height: number, showRulers: boolean): Partial<ViewState> {
  const layout = layoutFor(pattern);
  const gutter = showRulers ? GUTTER : 0;
  const availW = Math.max(50, width - gutter - 32);
  const availH = Math.max(50, height - gutter - 32);
  const zoom = Math.max(1.5, Math.min(60, Math.min(availW / layout.totalW, availH / layout.totalH)));
  return {
    zoom,
    panX: (availW - layout.totalW * zoom) / 2 + 16,
    panY: (availH - layout.totalH * zoom) / 2 + 16,
  };
}
